/* eslint-disable react/jsx-closing-tag-location */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import HVLogo from '../../assests/icons/logo.png';
import SmallHVLogo from '../../assests/icons/smallHvLogo.png';
import { footerListConfig, itemListConfig } from './config/MainDrawerConfig';
import subMenuConfig, { subDrawerInitialState } from './config/SubDrawerConfig';
import { DrawerFunctions, ItemListConfig } from '../../types/Drawer';
import getTooltipForDisabled, { isItemDisabled, isItemHidden, disabledFunction } from './utils';

function MainDrawer({
  drawerFunctions,
  setSubDrawerData,
  activePage,
  hiddenNavKeys,
  disabledNavKeys,
}: {
    drawerFunctions: DrawerFunctions;
    setSubDrawerData: any;
    activePage: string;
    hiddenNavKeys: string[];
    disabledNavKeys: string[];
  }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState('');
  const returnActiveClass = (key: string) => (activeMenuItem === key ? 'active' : '');
  // eslint-disable-next-line no-param-reassign
  drawerFunctions.collapse = () => { setIsCollapsed(!isCollapsed); };

  const itemOnClickGen = (item: ItemListConfig) => {
    if (isItemDisabled(item, disabledNavKeys)) {
      return disabledFunction;
    }
    const onClickFunc = get(drawerFunctions, `${item.onClickFunctionName}`, disabledFunction);
    const onClick = () => {
      if (item.subMenuKey) {
        setIsCollapsed(true);
        setSubDrawerData(subMenuConfig[item.subMenuKey]);
      } else {
        setSubDrawerData(subDrawerInitialState);
      }
      if (!item.external) setActiveMenuItem(item.key);
      onClickFunc();
    };
    return onClick;
  };

  // Remove hidden items
  let itemList = itemListConfig.filter((item) => !isItemHidden(item, hiddenNavKeys));

  itemList = itemList.map((item) => {
    const onClick = itemOnClickGen(item);
    return { ...item, onClick };
  });

  const footerList = footerListConfig.map((item) => {
    let onClick;
    if (isItemDisabled(item, disabledNavKeys)) {
      onClick = disabledFunction;
    } else {
      onClick = get(drawerFunctions, `${item.onClickFunctionName}`, disabledFunction);
    }
    return { ...item, onClick };
  });

  useEffect(() => {
    if (activePage) {
      const activeItem = itemList.find((item) => activePage === item.key);
      // Dont click if already active
      if (activeItem && activeItem.key !== activeMenuItem) activeItem.onClick();
    }
  }, [activePage]);

  return (
    <div id="drawer__container">
      <div id="drawer__logo_container" className={`${isCollapsed && 'collapsed'}`}>
        {!isCollapsed ? (
          <img src={HVLogo} alt="logo" />
        ) : (
          <img style={{ width: '28px', height: '32px' }} src={SmallHVLogo} alt="logo" />
        )}
      </div>
      <div id="drawer__items_container">
        {itemList.map(
          (item) => {
            const {
              key,
              text,
              onClick,
              icon,
              collapsedIcon,
              tooltip,
              id,
            } = item;
            const disabled = isItemDisabled(item, disabledNavKeys);
            return (
              <>
                {getTooltipForDisabled(<div
                  aria-hidden
                  key={id}
                  id={id}
                  onClick={onClick}
                  role="button"
                  className={`${returnActiveClass(key)} ${disabled && 'disabled'} ${isCollapsed && 'collapsed'}`}
                >
                  {isCollapsed ? collapsedIcon || icon : icon}
                  {!isCollapsed && <p>{text}</p>}
                  {isCollapsed && tooltip && (
                  <div className="tooltip">
                    <div className="tooltip_arrow" />
                    <div className="tooltip_body">{tooltip}</div>
                  </div>
                  )}
                </div>, disabled)}
              </>
            );
          },
        )}
      </div>
      <div id="drawer__footer_container">
        {footerList.map(
          (item) => {
            const {
              key,
              text,
              onClick,
              icon,
              collapsedIcon,
              tooltip,
              id,
            } = item;
            const disabled = isItemDisabled(item, disabledNavKeys);
            return (
              <>
                {getTooltipForDisabled(<div
                  aria-hidden
                  key={id}
                  id={id}
                  onClick={onClick}
                  role="button"
                  className={`${returnActiveClass(key)} ${disabled && 'disabled'} ${isCollapsed && 'collapsed'}`}
                >
                  {isCollapsed ? collapsedIcon || icon : icon}
                  {!isCollapsed && <p>{text}</p>}
                  {isCollapsed && tooltip && (
                  <div className="tooltip">
                    <div className="tooltip_arrow" />
                    <div className="tooltip_body">{tooltip}</div>
                  </div>
                  )}
                </div>, disabled)}
              </>
            );
          },
        )}
      </div>
    </div>
  );
}

MainDrawer.defaultProps = {
  hiddenNavKeys: [],
  disabledNavKeys: [],
};

MainDrawer.propTypes = {
  drawerFunctions: PropTypes.object.isRequired,
  setSubDrawerData: PropTypes.func.isRequired,
  activePage: PropTypes.string.isRequired,
  hiddenNavKeys: PropTypes.array,
  disabledNavKeys: PropTypes.array,
};

export default MainDrawer;
